import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Collapse,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { getFunctions, httpsCallable } from 'firebase/functions'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpIcon from '@mui/icons-material/Help'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import SecurityIcon from '@mui/icons-material/Security'
import TradeExitIcon from './TradeExitIcon'
import {
  didHitOrCrossLevel,
  getStopLossTakeProfitLevels,
  getTradeExitKind,
} from '../utils/tradeExit'
import { describeTradeRejection, formatTradeRejectReason } from '../utils/tradeRejection'
import TradeParamsCompact from './TradeParamsCompact'

// Trade Reject Reason Dialog Component
function TradeRejectReasonDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [brokerCodesExpanded, setBrokerCodesExpanded] = React.useState(false)

  const greenchartReasons = [
    {
      icon: <WarningIcon sx={{ color: '#FF9800' }} />,
      title: 'MAX_LIMIT',
      description: 'Maximum position limit reached',
      explanation:
        'You have reached the maximum number of open positions allowed by your auto-trade risk settings. Close some existing positions before opening new trades.',
    },
    {
      icon: <WarningIcon sx={{ color: '#FF9800' }} />,
      title: 'DAILY_LIMIT',
      description: 'Daily trading limit exceeded',
      explanation:
        'You have exceeded the maximum number of trades allowed per day by your auto-trade risk settings. Wait until the next trading day to place more trades.',
    },
    {
      icon: <SecurityIcon sx={{ color: '#9C27B0' }} />,
      title: 'HEDGING_DISABLED',
      description: 'Hedging is disabled on your account',
      explanation:
        'Your trading account has hedging disabled. You cannot open positions in the opposite direction of existing trades on the same symbol.',
    },
    {
      icon: <SecurityIcon sx={{ color: '#9C27B0' }} />,
      title: 'HEDGING_NOT_ALLOWED',
      description: 'Hedging not allowed for this trade',
      explanation:
        "This specific trade would create a hedging position which is not allowed by your auto-trade risk settings or your broker's symbol trading rules.",
    },
    {
      icon: <WarningIcon sx={{ color: '#FF9800' }} />,
      title: 'MAX_RISK',
      description: 'Maximum risk limit exceeded',
      explanation:
        'This trade would exceed your maximum risk per trade limit. Reduce the position size or adjust your stop loss to stay within risk limits.',
    },
    {
      icon: <WarningIcon sx={{ color: '#FF9800' }} />,
      title: 'MIN_LOT_SIZE',
      description: 'Position size below minimum lot size',
      explanation:
        'The requested position size is smaller than the minimum lot size allowed for this symbol. Increase the position size to meet the minimum requirements.',
    },
    {
      icon: <AccountBalanceIcon sx={{ color: '#f44336' }} />,
      title: 'NO_EQUITY',
      description: 'Insufficient account equity',
      explanation:
        'Your account does not have enough equity to open this position. Deposit more funds or reduce the position size.',
    },
    {
      icon: <AccountBalanceIcon sx={{ color: '#f44336' }} />,
      title: 'INSUFFICIENT_MARGIN',
      description: 'Insufficient margin available',
      explanation:
        'Your account does not have enough available margin to cover the required margin for this trade. Free up margin by closing positions or depositing funds.',
    },
    {
      icon: <AccountBalanceIcon sx={{ color: '#f44336' }} />,
      title: 'EXCEEDS_LEVERAGE_LIMIT',
      description: 'Trade exceeds leverage limit',
      explanation:
        'This trade would exceed the maximum leverage allowed for your account or this symbol. Reduce the position size or use lower leverage.',
    },
    {
      icon: <ErrorIcon sx={{ color: '#f44336' }} />,
      title: 'INVALID_STOP',
      description: 'Invalid stop loss or take profit',
      explanation:
        'The stop loss or take profit levels are invalid for this trade. Ensure they are within the allowed range and properly set relative to the entry price.',
    },
    {
      icon: <ErrorIcon sx={{ color: '#f44336' }} />,
      title: 'SYMBOL_NOT_FOUND',
      description: 'Symbol not found',
      explanation:
        'The symbol specified for this trade could not be found. This may happen if the symbol is not available on your broker account or if the symbol name mapping is incorrect.',
    },
    {
      icon: <ErrorIcon sx={{ color: '#f44336' }} />,
      title: 'CONNECTION_ERROR',
      description: 'Failed to connect to broker',
      explanation:
        'GreenChart was unable to establish a connection to MetaTrader at the time of this trade. This is usually a temporary issue. If it continues, verify that your MetaTrader account is active and reachable.',
    },
    {
      icon: <ErrorIcon sx={{ color: '#f44336' }} />,
      title: 'SERVER_ERROR',
      description: 'Server error occurred',
      explanation:
        'A temporary server error prevented the trade from being executed. Please try again in a few moments. If the problem persists, contact support.',
    },
  ]

  const commonBrokerErrors = [
    {
      code: 130,
      title: 'Invalid Stops',
      description:
        'Stop loss or take profit is too close to the current price, or the levels are invalid for this symbol.',
    },
    {
      code: 131,
      title: 'Invalid Volume',
      description:
        'The lot size is outside the allowed range for this symbol (too small or too large).',
    },
    {
      code: 132,
      title: 'Market Closed',
      description:
        'The market is currently closed for this symbol. Check trading hours for your broker.',
    },
    {
      code: 134,
      title: 'Not Enough Money',
      description: 'Your account does not have enough free margin to open this position.',
    },
    {
      code: 10016,
      title: 'Invalid SL/TP (MT5)',
      description: 'The stop loss or take profit values are rejected by the MT5 trade server.',
    },
    {
      code: 10018,
      title: 'Market Closed (MT5)',
      description: 'The market session is closed on the MT5 trade server.',
    },
    {
      code: 10019,
      title: 'Not Enough Money (MT5)',
      description: 'Insufficient funds on the MT5 account to execute the trade.',
    },
  ]

  const fullMt4Codes: [number, string, string][] = [
    [0, 'ERR_NO_ERROR', 'No error'],
    [64, 'ERR_ACCOUNT_DISABLED', 'Account disabled'],
    [65, 'ERR_INVALID_ACCOUNT', 'Invalid account'],
    [128, 'ERR_TRADE_TIMEOUT', 'Trade timeout'],
    [129, 'ERR_INVALID_PRICE', 'Invalid price'],
    [130, 'ERR_INVALID_STOPS', 'Invalid stops (SL/TP too close or invalid)'],
    [131, 'ERR_INVALID_TRADE_VOLUME', 'Invalid trade volume'],
    [132, 'ERR_MARKET_CLOSED', 'Market is closed'],
    [133, 'ERR_TRADE_DISABLED', 'Trade is disabled'],
    [134, 'ERR_NOT_ENOUGH_MONEY', 'Not enough money'],
    [135, 'ERR_PRICE_CHANGED', 'Price changed'],
    [136, 'ERR_OFF_QUOTES', 'Off quotes'],
    [137, 'ERR_BROKER_BUSY', 'Broker is busy'],
    [138, 'ERR_REQUOTE', 'Requote'],
    [139, 'ERR_ORDER_LOCKED', 'Order is locked'],
    [140, 'ERR_LONG_POSITIONS_ONLY', 'Only buy orders allowed'],
    [141, 'ERR_TOO_MANY_REQUESTS', 'Too many requests'],
    [145, 'ERR_MODIFICATION_DENIED', 'Modification denied — order too close to market'],
    [146, 'ERR_TRADE_CONTEXT_BUSY', 'Trade context is busy'],
    [147, 'ERR_EXPIRATION_DENIED', 'Expiration denied by broker'],
    [148, 'ERR_TOO_MANY_ORDERS', 'Too many open/pending orders'],
    [149, 'ERR_HEDGE_PROHIBITED', 'Hedging is prohibited'],
    [150, 'ERR_FIFO_CLOSE', 'Prohibited by FIFO rules'],
  ]

  const fullMt5Codes: [number, string, string][] = [
    [10004, 'TRADE_RETCODE_REQUOTE', 'Requote — prices changed'],
    [10006, 'TRADE_RETCODE_REJECT', 'Request rejected'],
    [10007, 'TRADE_RETCODE_CANCEL', 'Request canceled by trader'],
    [10008, 'TRADE_RETCODE_PLACED', 'Order placed'],
    [10009, 'TRADE_RETCODE_DONE', 'Request completed'],
    [10010, 'TRADE_RETCODE_DONE_PARTIAL', 'Partially completed'],
    [10011, 'TRADE_RETCODE_ERROR', 'Request processing error'],
    [10012, 'TRADE_RETCODE_TIMEOUT', 'Request timed out'],
    [10013, 'TRADE_RETCODE_INVALID', 'Invalid request'],
    [10014, 'TRADE_RETCODE_INVALID_VOLUME', 'Invalid volume'],
    [10015, 'TRADE_RETCODE_INVALID_PRICE', 'Invalid price'],
    [10016, 'TRADE_RETCODE_INVALID_STOPS', 'Invalid SL or TP'],
    [10017, 'TRADE_RETCODE_TRADE_DISABLED', 'Trading disabled'],
    [10018, 'TRADE_RETCODE_MARKET_CLOSED', 'Market closed'],
    [10019, 'TRADE_RETCODE_NO_MONEY', 'Not enough money'],
    [10020, 'TRADE_RETCODE_PRICE_CHANGED', 'Prices changed'],
    [10021, 'TRADE_RETCODE_PRICE_OFF', 'No quotes available'],
    [10022, 'TRADE_RETCODE_INVALID_EXPIRATION', 'Invalid expiration'],
    [10023, 'TRADE_RETCODE_ORDER_CHANGED', 'Order state changed'],
    [10024, 'TRADE_RETCODE_TOO_MANY_REQUESTS', 'Too many requests'],
    [10025, 'TRADE_RETCODE_NO_CHANGES', 'No changes in request'],
    [10026, 'TRADE_RETCODE_SERVER_DISABLES_AT', 'Autotrading disabled by server'],
    [10027, 'TRADE_RETCODE_CLIENT_DISABLES_AT', 'Autotrading disabled by client'],
    [10028, 'TRADE_RETCODE_LOCKED', 'Request locked'],
    [10029, 'TRADE_RETCODE_FROZEN', 'Order or position frozen'],
    [10030, 'TRADE_RETCODE_INVALID_FILL', 'Invalid fill type'],
    [10031, 'TRADE_RETCODE_CONNECTION', 'No connection'],
    [10032, 'TRADE_RETCODE_ONLY_REAL', 'Only for live accounts'],
    [10033, 'TRADE_RETCODE_LIMIT_ORDERS', 'Pending orders limit reached'],
    [10034, 'TRADE_RETCODE_LIMIT_VOLUME', 'Volume limit reached'],
    [10035, 'TRADE_RETCODE_INVALID_ORDER', 'Invalid order type'],
    [10036, 'TRADE_RETCODE_POSITION_CLOSED', 'Position already closed'],
    [10038, 'TRADE_RETCODE_CLOSE_ORDER_EXIST', 'Close order already exists'],
    [10039, 'TRADE_RETCODE_LIMIT_POSITIONS', 'Max positions reached'],
    [10040, 'TRADE_RETCODE_REJECT_CANCEL', 'Activation rejected'],
    [10041, 'TRADE_RETCODE_LONG_ONLY', 'Only long positions allowed'],
    [10042, 'TRADE_RETCODE_SHORT_ONLY', 'Only short positions allowed'],
    [10043, 'TRADE_RETCODE_CLOSE_ONLY', 'Only close operations allowed'],
    [10044, 'TRADE_RETCODE_FIFO_CLOSE', 'Close not allowed (FIFO)'],
    [10045, 'TRADE_RETCODE_HEDGE_PROHIBITED', 'Hedge close not allowed (FIFO)'],
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography
          variant="h6"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <HelpIcon color="primary" />
          Trade Rejection Reasons
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Section 1: GreenChart Validation Errors */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>
          GreenChart Validation Errors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These are pre-trade risk management checks performed before the order is sent to your
          broker.
        </Typography>
        <List disablePadding>
          {greenchartReasons.map((reason, index) => (
            <ListItem key={index} sx={{ alignItems: 'flex-start', py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{reason.icon}</ListItemIcon>
              <ListItemText
                primary={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>
                      {formatTradeRejectReason(reason.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {reason.description}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {reason.explanation}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 3 }} />

        {/* Section 2: Broker (MetaTrader) Errors */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Broker (MetaTrader) Errors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These errors are returned by the MetaTrader trade server after the order was submitted to
          your broker.
        </Typography>
        <List disablePadding>
          {commonBrokerErrors.map((err) => (
            <ListItem key={err.code} sx={{ alignItems: 'flex-start', py: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                <ErrorIcon sx={{ color: '#f44336' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>
                    {err.title} (code {err.code})
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {err.description}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>

        {/* Learn More — full code tables */}
        <Button
          size="small"
          onClick={() => setBrokerCodesExpanded((prev) => !prev)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: brokerCodesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          }
          sx={{ mt: 1, mb: 1, textTransform: 'none' }}
        >
          {brokerCodesExpanded ? 'Hide' : 'Show'} All Error Codes
        </Button>

        <Collapse in={brokerCodesExpanded} timeout="auto" unmountOnExit>
          {/* MT4 codes */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
            MT4 Error Codes
          </Typography>
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', mb: 3 }}
          >
            <thead>
              <tr>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Code
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Constant
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Description
                </Box>
              </tr>
            </thead>
            <tbody>
              {fullMt4Codes.map(([code, constant, desc]) => (
                <tr key={code}>
                  <Box
                    component="td"
                    sx={{
                      p: 0.75,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {code}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 0.75,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {constant}
                  </Box>
                  <Box
                    component="td"
                    sx={{ p: 0.75, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    {desc}
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>

          {/* MT5 codes */}
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1, mb: 1 }}>
            MT5 Error Codes
          </Typography>
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
          >
            <thead>
              <tr>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Code
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Constant
                </Box>
                <Box
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 0.75,
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  Description
                </Box>
              </tr>
            </thead>
            <tbody>
              {fullMt5Codes.map(([code, constant, desc]) => (
                <tr key={code}>
                  <Box
                    component="td"
                    sx={{
                      p: 0.75,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {code}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 0.75,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {constant}
                  </Box>
                  <Box
                    component="td"
                    sx={{ p: 0.75, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    {desc}
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Trade information type
interface TradeInfo {
  sl: number // stop loss
  tp: number // take profit
  entryPrice: number
  entryTime: number
  exitPrice?: number
  exitTime?: number
  lotSize: number
  profit?: number // only present for OPEN or CLOSED trades
  status: 'REJECTED' | 'CLOSED' | 'PENDING' | 'OPEN'
  reason?: string // only present when status is REJECTED
  closeNotificationId?: string // present when trade was closed via alert exit notification
  exitReason?: string // present when a backend closed the position for a named reason, e.g. 'force_exit'
  errorSource?: 'greenchart' | 'metatrader'
  errorCode?: number
  errorMessage?: string
}

// History item type
interface HistoryItem {
  time: number
  type: 'buy' | 'sell'
  price: number
  trade?: TradeInfo
  alertSnapshot?: any
}

// Day history item type
interface DayHistoryItem {
  date: string
  history: HistoryItem[]
  alert?: any
}

interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalDays: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

interface AlertHistoryResponse {
  status: number
  data: DayHistoryItem[]
  pagination: PaginationInfo
}

interface AlertHistoryProps {
  alertId: string
  isActive: boolean
  tradingAccountId?: string
  refreshTrigger?: number
}

const AlertHistory: React.FC<AlertHistoryProps> = ({ alertId, refreshTrigger = 0 }) => {
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null)
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const fetchHistory = useCallback(
    async (page: number = 0) => {
      if (!alertId) return

      if (page > 0) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setError(null)
      }

      try {
        const functions = getFunctions()
        const getAlertHistory = httpsCallable(functions, 'getAlertHistory')

        const result = await getAlertHistory({ alertId, page })
        const response = result.data as AlertHistoryResponse

        if (response.status === 200) {
          const dayHistoryItems = response.data
          // Store pagination info
          setPaginationInfo(response.pagination)

          // Flatten the day history items into individual history items
          const flattenedHistoryItems: HistoryItem[] = dayHistoryItems.flatMap((dayItem) =>
            dayItem.history.map((item) => ({
              ...item,
              dayDate: dayItem.date,
              alertSnapshot: dayItem.alert,
            }))
          )

          // Update history state
          if (page > 0) {
            // Append to existing history
            setHistory((prev) => [...prev, ...flattenedHistoryItems])
          } else {
            // Replace history
            setHistory(flattenedHistoryItems)
          }
        } else {
          setError('Failed to load alert history')
        }
      } catch (err) {
        console.error('Error fetching alert history:', err)
        setError('Failed to load alert history')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [alertId]
  )

  useEffect(() => {
    fetchHistory(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId])

  // Refresh history data
  const handleRefresh = useCallback(async () => {
    if (!alertId) return

    try {
      // Reload history starting from page 0
      await fetchHistory(0)
    } catch (err) {
      console.error('Error refreshing history:', err)
      setError('Failed to refresh history')
    }
  }, [alertId, fetchHistory])

  // Handle refresh trigger from parent
  useEffect(() => {
    if (refreshTrigger > 0) {
      handleRefresh()
    }
  }, [refreshTrigger, handleRefresh])

  // Load more history
  const handleLoadMore = useCallback(() => {
    const nextPage = (paginationInfo?.currentPage || 0) + 1
    fetchHistory(nextPage)
  }, [fetchHistory, paginationInfo?.currentPage])

  // Dialog handlers
  const handleOpenReasonDialog = () => {
    setReasonDialogOpen(true)
  }

  const handleCloseReasonDialog = () => {
    setReasonDialogOpen(false)
  }

  // Format date and time
  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }

  const formatPrice = (value?: number) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 5,
    }).format(value)
  }

  const getSideColor = (type: 'buy' | 'sell') => (type === 'buy' ? '#4CAF50' : '#f44336')

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (history.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No history found for this alert</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <List
        disablePadding
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 0,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        {history.map((item, index) => (
          <React.Fragment key={`${item.time}-${index}`}>
            {(() => {
              const rowKey = `${item.time}-${index}`
              const rejection =
                item.trade?.status === 'REJECTED' ? describeTradeRejection(item.trade) : null
              const isExpanded = expandedKey === rowKey
              const sideColor = getSideColor(item.type)
              const performanceColor =
                item.trade?.profit === undefined || item.trade.profit === 0
                  ? 'rgba(255, 255, 255, 0.15)'
                  : item.trade.profit > 0
                    ? '#4CAF50'
                    : '#f44336'
              const exitKind = getTradeExitKind(item.trade, item.type)
              const { slPrice, tpPrice } = getStopLossTakeProfitLevels(item.trade, item.type)
              const slHit =
                item.trade?.status === 'CLOSED' &&
                didHitOrCrossLevel(item.type, 'sl', item.trade.exitPrice, slPrice)
              const tpHit =
                item.trade?.status === 'CLOSED' &&
                didHitOrCrossLevel(item.type, 'tp', item.trade.exitPrice, tpPrice)

              return (
                <ListItem
                  disablePadding
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <ListItemButton
                    onClick={() => setExpandedKey((prev) => (prev === rowKey ? null : rowKey))}
                    sx={{
                      py: 1.25,
                      px: 1,
                      pl: 1.75,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        borderRadius: '999px',
                        backgroundColor: performanceColor,
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.75 }}
                    >
                      {/* Row 1: Type + Lot (left), Datetime (right) */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              color: sideColor,
                              flexShrink: 0,
                            }}
                          >
                            {item.type[0].toUpperCase() + item.type.slice(1)}
                          </Typography>

                          {item.trade?.lotSize !== undefined && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                color: sideColor,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {Number(item.trade.lotSize).toLocaleString('en-US', {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 8,
                              })}
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatDateTime(item.time)}
                        </Typography>
                      </Box>

                      {/* Row 2: Status + Profit (left), Entry -> Exit (right) */}
                      {item.trade && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 'bold',
                                color:
                                  item.trade.status === 'REJECTED'
                                    ? '#f44336'
                                    : item.trade.status === 'PENDING'
                                      ? '#FF9800'
                                      : 'white',
                              }}
                            >
                              {item.trade.status}
                            </Typography>

                            {(item.trade.status === 'OPEN' || item.trade.status === 'CLOSED') &&
                              item.trade.profit !== undefined && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: item.trade.profit >= 0 ? '#4CAF50' : '#f44336',
                                  }}
                                >
                                  ${item.trade.profit}
                                </Typography>
                              )}

                            {rejection && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip
                                  title={rejection.tooltip ?? ''}
                                  disableHoverListener={!rejection.tooltip}
                                  arrow
                                >
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ fontStyle: 'italic' }}
                                  >
                                    {rejection.text}
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleOpenReasonDialog()
                                  }}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                    p: 0.25,
                                  }}
                                >
                                  <HelpIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>

                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {item.trade.exitPrice === undefined
                                ? `$${formatPrice(item.trade.entryPrice)}`
                                : `$${formatPrice(item.trade.entryPrice)} → $${formatPrice(item.trade.exitPrice)}`}
                            </Typography>
                            <TradeExitIcon exitKind={exitKind} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </ListItemButton>

                  {/* Expanded details */}
                  {item.trade && (
                    <Collapse
                      in={isExpanded}
                      timeout="auto"
                      unmountOnExit
                      sx={{
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '1px',
                          borderRadius: '999px',
                          backgroundColor: performanceColor,
                        },
                      }}
                    >
                      <Box sx={{ px: 2, pb: 1.25 }}>
                        {/* TRADE section header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              fontWeight: 700,
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              color: 'rgba(255,255,255,0.28)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Trade
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: '1px',
                              backgroundColor: 'rgba(255,255,255,0.07)',
                            }}
                          />
                        </Box>

                        {/* 2-col grid of trade fields */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            rowGap: '4px',
                            columnGap: '12px',
                          }}
                        >
                          {[
                            {
                              label: 'Entry',
                              value: `$${formatPrice(item.trade?.entryPrice)}`,
                              color: 'rgba(255,255,255,0.82)',
                            },
                            {
                              label: 'Exit',
                              value:
                                item.trade?.exitPrice !== undefined
                                  ? `$${formatPrice(item.trade.exitPrice)}`
                                  : '—',
                              color: 'rgba(255,255,255,0.82)',
                            },
                            {
                              label: 'SL',
                              value: slPrice !== undefined ? `$${formatPrice(slPrice)}` : '—',
                              color: slHit ? '#f44336' : 'rgba(255,255,255,0.82)',
                            },
                            {
                              label: 'TP',
                              value: tpPrice !== undefined ? `$${formatPrice(tpPrice)}` : '—',
                              color: tpHit ? '#4CAF50' : 'rgba(255,255,255,0.82)',
                            },
                            ...(item.trade?.entryTime
                              ? [
                                  {
                                    label: 'Opened',
                                    value: formatDateTime(item.trade.entryTime),
                                    color: 'rgba(255,255,255,0.82)' as string,
                                  },
                                ]
                              : []),
                            ...(item.trade?.exitTime
                              ? [
                                  {
                                    label: 'Closed',
                                    value: formatDateTime(item.trade.exitTime),
                                    color: 'rgba(255,255,255,0.82)' as string,
                                  },
                                ]
                              : []),
                          ].map((field, fi) => (
                            <Box
                              key={fi}
                              sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: 'rgba(255,255,255,0.38)',
                                  lineHeight: 1.4,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {field.label}:
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: field.color,
                                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                                  lineHeight: 1.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {field.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {item.alertSnapshot?.trading?.options && (
                        <Box sx={{ px: 2, pb: 1.5 }}>
                          <TradeParamsCompact alert={item.alertSnapshot} />
                        </Box>
                      )}
                    </Collapse>
                  )}
                </ListItem>
              )
            })()}
            {index < history.length - 1 && (
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
            )}
          </React.Fragment>
        ))}

        {/* Load More Button */}
        {paginationInfo?.hasNextPage && (
          <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </List>

      {/* Trade Reject Reason Dialog */}
      <TradeRejectReasonDialog open={reasonDialogOpen} onClose={handleCloseReasonDialog} />
    </Box>
  )
}

export default AlertHistory
